import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '../../hooks/use-toast';

// ── Push notification helper ──
const sendPushNotification = (title, body) => {
  if (Notification.permission !== 'granted' || document.hasFocus()) return;
  try {
    const n = new Notification(title, {
      body,
      icon: '/favicon.ico',
      tag: 'tekstar-ride', // reuse same slot so they don't stack
    });
    n.onclick = () => { window.focus(); n.close(); };
  } catch (e) {
    // Safari / restrictive browsers – silently ignore
  }
};

const useRideTracking = () => {
  const navigate = useNavigate();
  const [ride, setRide] = useState(null);
  const [driverLocation, setDriverLocation] = useState(null);
  const [status, setStatus] = useState('pending');
  const [eta, setEta] = useState(null);
  const [statusMessage, setStatusMessage] = useState('Finding a driver...');
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropoffCoords, setDropoffCoords] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(Notification.permission === 'granted');
  const wsRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const locationPollRef = useRef(null);
  const previousStatusRef = useRef('pending');
  const lastWsLocationRef = useRef(0);
  const etaAlertSentRef = useRef(false); // prevent duplicate "almost here" alerts

  const pollRideStatus = useCallback(async (rideId) => {
    try {
      const api = (await import('../../utils/api')).default;
      const response = await api.get(`/rides/${rideId}`);
      if (response.data.ride) {
        const newRide = response.data.ride;
        const newStatus = newRide.status;
        
        if (newStatus !== previousStatusRef.current) {
          console.log(`Ride status changed: ${previousStatusRef.current} -> ${newStatus}`);
          
          if (newStatus === 'accepted' && previousStatusRef.current === 'pending') {
            toast({
              title: 'Driver Found!',
              description: `${newRide.driver?.name || 'A driver'} has accepted your ride!`,
            });
            setStatusMessage('Driver has accepted your ride!');
            sendPushNotification('Driver Found!', `${newRide.driver?.name || 'A driver'} has accepted your ride!`);
          } else if (newStatus === 'arriving') {
            toast({
              title: 'Driver On The Way',
              description: 'Your driver is heading to pick you up',
            });
            setStatusMessage('Driver is on the way!');
            sendPushNotification('Driver On The Way', 'Your driver is heading to your pickup point');
          } else if (newStatus === 'in_progress') {
            toast({
              title: 'Ride Started',
              description: 'You are on your way to your destination',
            });
            setStatusMessage('Heading to destination');
            sendPushNotification('Ride Started', 'You are on your way to your destination');
          } else if (newStatus === 'completed') {
            toast({
              title: 'Ride Completed',
              description: 'Thanks for riding with Tekstar!',
            });
            setStatusMessage('Ride completed');
            setShowReviewModal(true);
            sendPushNotification('Ride Completed', 'Thanks for riding with Tekstar! Tap to rate your experience.');
          } else if (newStatus === 'cancelled') {
            toast({
              title: 'Ride Cancelled',
              description: 'Your ride has been cancelled',
              variant: 'destructive',
            });
            setStatusMessage('Ride cancelled');
            sendPushNotification('Ride Cancelled', 'Your ride has been cancelled');
          }
          
          previousStatusRef.current = newStatus;
        }
        
        setRide(newRide);
        setStatus(newStatus);
      }
    } catch (error) {
      console.error('Error polling ride:', error);
    }
  }, []);

  useEffect(() => {
    const rideId = localStorage.getItem('current_ride_id');
    
    if (!rideId) {
      navigate('/');
      return;
    }

    const fetchRideData = async (retryCount = 0) => {
      const maxRetries = 5;
      const retryDelay = 1000;
      
      try {
        const api = (await import('../../utils/api')).default;
        const response = await api.get(`/rides/${rideId}`);
        const rideData = response.data.ride;
        
        setRide(rideData);
        setStatus(rideData.status || 'pending');
        
        // Use stored coordinates from ride data first
        const extractCoord = (sources) => {
          for (const src of sources) {
            if (src && typeof src === 'object') {
              const lat = src.lat ?? src.latitude;
              const lng = src.lng ?? src.longitude;
              if (lat != null && lng != null) return { latitude: Number(lat), longitude: Number(lng) };
            }
          }
          return null;
        };

        const pickup = extractCoord([rideData.pickupCoords, rideData.pickupLocation, rideData.pickupCoordinates]);
        const dropoff = extractCoord([rideData.dropoffCoords, rideData.dropoffLocation, rideData.dropoffCoordinates]);

        if (pickup) setPickupCoords(pickup);
        if (dropoff) setDropoffCoords(dropoff);

        // Fallback to geocoding only if stored coords are missing
        if (!pickup && rideData.pickup) {
          try {
            const pickupResponse = await api.get('/maps/geocode/forward', { params: { query: rideData.pickup } });
            if (pickupResponse.data.results?.length > 0) {
              setPickupCoords({ longitude: pickupResponse.data.results[0].longitude, latitude: pickupResponse.data.results[0].latitude });
            }
          } catch (e) { /* geocode fallback failed */ }
        }
        if (!dropoff && rideData.dropoff) {
          try {
            const dropoffResponse = await api.get('/maps/geocode/forward', { params: { query: rideData.dropoff } });
            if (dropoffResponse.data.results?.length > 0) {
              setDropoffCoords({ longitude: dropoffResponse.data.results[0].longitude, latitude: dropoffResponse.data.results[0].latitude });
            }
          } catch (e) { /* geocode fallback failed */ }
        }
      } catch (error) {
        console.error('Error fetching ride data:', error);
        
        if (retryCount < maxRetries) {
          console.log(`Retrying fetchRideData (${retryCount + 1}/${maxRetries})...`);
          setTimeout(() => fetchRideData(retryCount + 1), retryDelay);
        } else {
          toast({
            title: 'Error Loading Ride',
            description: 'Unable to load ride details. Please try refreshing the page.',
            variant: 'destructive',
          });
        }
      }
    };
    
    fetchRideData();

    // Request notification permission
    if (Notification.permission === 'default') {
      Notification.requestPermission().then((perm) => {
        setNotificationsEnabled(perm === 'granted');
      });
    }

    pollIntervalRef.current = setInterval(() => {
      pollRideStatus(rideId);
    }, 3000);

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    let wsUrl;
    
    if (backendUrl) {
      wsUrl = backendUrl.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ws/track/' + rideId;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/ws/track/${rideId}`;
    }
    
    console.log('Connecting to WebSocket:', wsUrl);
    
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      toast({
        title: 'Live Tracking Active',
        description: 'You can now see your ride in real-time',
      });
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('WebSocket message:', data);

      switch (data.type) {
        case 'ride_info':
          setRide(data.ride);
          setStatus(data.ride.status);
          break;
          
        case 'location_update':
          setDriverLocation(data.driver_location);
          lastWsLocationRef.current = Date.now();
          if (data.eta_minutes !== undefined) {
            setEta(data.eta_minutes);
            // Push notification when driver is almost here (ETA <= 2 min)
            if (data.eta_minutes <= 2 && !etaAlertSentRef.current) {
              etaAlertSentRef.current = true;
              sendPushNotification('Driver Almost Here!', `Your driver is ${data.eta_minutes} minute${data.eta_minutes !== 1 ? 's' : ''} away`);
            }
          }
          break;
          
        case 'status_update':
          setStatus(data.status);
          setStatusMessage(data.message);
          toast({
            title: 'Status Update',
            description: data.message,
          });
          sendPushNotification('Ride Update', data.message);
          
          if (data.status === 'completed') {
            setShowReviewModal(true);
          }
          // Reset ETA alert flag when status changes (e.g. from arriving to in_progress)
          etaAlertSentRef.current = false;
          break;
          
        case 'error':
          toast({
            title: 'Error',
            description: data.message,
            variant: 'destructive',
          });
          break;
        default:
          break;
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      toast({
        title: 'Connection Error',
        description: 'Unable to connect to live tracking. Using fallback mode.',
        variant: 'destructive',
      });
      
      const pollInterval = setInterval(async () => {
        try {
          const api = (await import('../../utils/api')).default;
          const response = await api.get(`/rides/${rideId}`);
          if (response.data.ride) {
            setRide(response.data.ride);
            setStatus(response.data.ride.status);
          }
        } catch (error) {
          console.error('Error polling ride:', error);
        }
      }, 5000);
      
      wsRef.current = { pollInterval };
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
    };

    // Fallback: poll driver location if no WebSocket location updates received in 10s
    locationPollRef.current = setInterval(async () => {
      if (Date.now() - lastWsLocationRef.current < 10000) return; // WS is working fine
      try {
        const api = (await import('../../utils/api')).default;
        const res = await api.get(`/rides/${rideId}/driver-location`);
        if (res.data.driver_location) {
          setDriverLocation(res.data.driver_location);
        }
      } catch (e) { /* silent */ }
    }, 8000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (locationPollRef.current) {
        clearInterval(locationPollRef.current);
      }
      if (wsRef.current) {
        if (wsRef.current.close) {
          wsRef.current.close();
        }
        if (wsRef.current.pollInterval) {
          clearInterval(wsRef.current.pollInterval);
        }
      }
    };
  }, [navigate, pollRideStatus]);

  return {
    ride,
    driverLocation,
    status,
    eta,
    statusMessage,
    pickupCoords,
    dropoffCoords,
    showReviewModal,
    setShowReviewModal,
    notificationsEnabled,
  };
};

export default useRideTracking;
